import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import PortalLayout from "@/components/PortalLayout";
import LoginPage from "@/pages/LoginPage";
import PasswordSetupPage from "@/pages/PasswordSetupPage";
import DashboardPage from "@/pages/admin/DashboardPage";
import ExecutiveSummaryPage from "@/pages/admin/ExecutiveSummaryPage";
import UsersPage from "@/pages/admin/UsersPage";
import UserDetailPage from "@/pages/admin/UserDetailPage";
import QuoteRequestPage from "@/pages/admin/QuoteRequestPage";
import ProjectCreatePage from "@/pages/admin/ProjectCreatePage";
import ProjectsPage from "@/pages/admin/ProjectsPage";
import PlansPage from "@/pages/admin/PlansPage";
import ViewerPage from "@/pages/ViewerPage";
import AssignerPage from "@/pages/AssignerPage";
import UreticiPage from "@/pages/UreticiPage";
import AssignerDashboardPage from "@/pages/AssignerDashboardPage";
import PaymentPlanPage from "@/pages/PaymentPlanPage";
import ProfitAnalysisPage from "@/pages/ProfitAnalysisPage";
import KurulumTimelinePage from "@/pages/KurulumTimelinePage";
import FieldPhotosPage from "@/pages/FieldPhotosPage";
import SetupTvPage from "@/pages/SetupTvPage";
import OrdersPage from "@/pages/OrdersPage";
import SetupDashboardPage from "@/pages/admin/SetupDashboardPage";
import AssignerSetupDashboardPage from "@/pages/AssignerSetupDashboardPage";
import TaseronPage from "@/pages/TaseronPage";
import AssignerSettingsPage from "@/pages/AssignerSettingsPage";
import ProducerDashboardPage from "@/pages/ProducerDashboardPage";
import ProjectDetailPage from "@/pages/admin/ProjectDetailPage";
import RequestsPage from "@/pages/admin/RequestsPage";
import CompareRequestPage from "@/pages/admin/CompareRequestPage";
import SupplierRequestsPage from "@/pages/supplier/SupplierRequestsPage";
import SupplierDashboardPage from "@/pages/supplier/SupplierDashboardPage";
import SupplierRequestDetailPage from "@/pages/supplier/SupplierRequestDetailPage";
import LandingPage from "@/pages/LandingPage";
import LeadsPage from "@/pages/admin/LeadsPage";
import RegisterPage from "@/pages/RegisterPage";
import SupplierAppsPage from "@/pages/admin/SupplierAppsPage";
import ReferencesPage from "@/pages/admin/ReferencesPage";
import CustomerProjectsPage from "@/pages/customer/CustomerProjectsPage";
import CustomerProjectCreatePage from "@/pages/customer/CustomerProjectCreatePage";
import CustomerProjectDetailPage from "@/pages/customer/CustomerProjectDetailPage";
import CustomerDashboardPage from "@/pages/customer/CustomerDashboardPage";
import CustomersPage from "@/pages/admin/CustomersPage";
import AdminCustomerDetailPage from "@/pages/admin/AdminCustomerDetailPage";
import InvitesPage from "@/pages/admin/InvitesPage";
import ChatsPage from "@/pages/admin/ChatsPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminFaqPage from "@/pages/admin/AdminFaqPage";
import SupplierFaqPage from "@/pages/supplier/SupplierFaqPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/admin/SettingsPage";
import TeamPage from "@/pages/admin/TeamPage";
import ApprovalsPage from "@/pages/admin/ApprovalsPage";
import ApprovalPublicPage from "@/pages/ApprovalPublicPage";
import PriceProposalPublicPage from "@/pages/PriceProposalPublicPage";
import TransferApprovalPublicPage from "@/pages/TransferApprovalPublicPage";
import { hasPerm, firstAllowedPath } from "@/lib/perms";

const Protected = ({ role, roles, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#F5F7FA]" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const Perm = ({ perm, children }) => {
  const { user } = useAuth();
  if (user?.role === "superadmin" && !hasPerm(user, perm)) return <Navigate to={firstAllowedPath(user)} replace />;
  return children;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#F5F7FA]" />;
  if (!user) return <LandingPage />;
  return <Navigate to={user.role === "superadmin" ? firstAllowedPath(user) : user.role === "customer" ? "/musteri/dashboard" : user.role === "viewer" ? "/izleyici" : user.role === "assigner" ? "/atayici" : user.role === "uretici" ? "/uretici" : user.role === "taseron" ? "/taseron" : "/tedarikci/dashboard"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/kayit" element={<RegisterPage />} />
          <Route path="/sifre-olustur/:token" element={<PasswordSetupPage />} />
          <Route path="/davet/:token" element={<InviteAcceptPage />} />
          <Route path="/onay/:token" element={<ApprovalPublicPage />} />
          <Route path="/fiyat-oneri/:token" element={<PriceProposalPublicPage />} />
          <Route path="/devir-onay/:token" element={<TransferApprovalPublicPage />} />
          <Route path="/izleyici" element={<Protected role="viewer"><ViewerPage /></Protected>} />
          <Route path="/atayici" element={<Protected role="assigner"><AssignerPage /></Protected>} />
          <Route path="/atayici/dashboard" element={<Protected role="assigner"><AssignerDashboardPage /></Protected>} />
          <Route path="/atayici/setup-dashboard" element={<Protected role="assigner"><AssignerSetupDashboardPage /></Protected>} />
          <Route path="/taseron" element={<Protected role="taseron"><TaseronPage /></Protected>} />
          <Route path="/atayici/odeme-plani" element={<Protected role="assigner"><PaymentPlanPage /></Protected>} />
          <Route path="/atayici/kar-zarar" element={<Protected role="assigner"><ProfitAnalysisPage /></Protected>} />
          <Route path="/atayici/kurulum-takvimi" element={<Protected role="assigner"><KurulumTimelinePage /></Protected>} />
          <Route path="/saha-fotograflari" element={<Protected roles={["assigner", "uretici", "superadmin"]}><FieldPhotosPage /></Protected>} />
          <Route path="/setup-tv" element={<SetupTvPage />} />
          <Route path="/setupdashboard" element={<SetupTvPage />} />
          <Route path="/projeler" element={<AssignerPage publicMode />} />
          <Route path="/siparisler" element={<Protected roles={["assigner", "uretici", "superadmin"]}><OrdersPage /></Protected>} />
          <Route path="/atayici/ayarlar" element={<Protected role="assigner"><AssignerSettingsPage /></Protected>} />
          <Route path="/atayici/tedarikciler" element={<Protected role="assigner"><ProducerDashboardPage /></Protected>} />
          <Route path="/uretici" element={<Protected role="uretici"><UreticiPage /></Protected>} />
          <Route
            path="/admin"
            element={
              <Protected role="superadmin">
                <PortalLayout />
              </Protected>
            }
          >
            <Route path="dashboard" element={<Perm perm="dashboard"><DashboardPage /></Perm>} />
            <Route path="kurulum" element={<Perm perm="projeler"><SetupDashboardPage /></Perm>} />
            <Route path="ozet" element={<Perm perm="dashboard"><ExecutiveSummaryPage /></Perm>} />
            <Route path="kullanicilar" element={<Perm perm="tedarikciler"><UsersPage /></Perm>} />
            <Route path="kullanicilar/:id" element={<Perm perm="tedarikciler"><UserDetailPage /></Perm>} />
            <Route path="musteriler" element={<Perm perm="musteriler"><CustomersPage /></Perm>} />
            <Route path="musteriler/:id" element={<Perm perm="musteriler"><AdminCustomerDetailPage /></Perm>} />
            <Route path="davetler" element={<Perm perm="davetler"><InvitesPage /></Perm>} />
            <Route path="sohbetler" element={<Perm perm="sohbetler"><ChatsPage /></Perm>} />
            <Route path="proje-olustur" element={<Perm perm="projeler"><ProjectCreatePage /></Perm>} />
            <Route path="projeler" element={<Perm perm="projeler"><ProjectsPage /></Perm>} />
            <Route path="projeler/:id" element={<Perm perm="projeler"><ProjectDetailPage /></Perm>} />
            <Route path="planlar" element={<Perm perm="projeler"><PlansPage /></Perm>} />
            <Route path="teklif-al" element={<Perm perm="teklifler"><QuoteRequestPage /></Perm>} />
            <Route path="teklifler" element={<Perm perm="teklifler"><RequestsPage /></Perm>} />
            <Route path="teklifler/:id" element={<Perm perm="teklifler"><CompareRequestPage /></Perm>} />
            <Route path="teklif-onayi" element={<Perm perm="teklifler"><ApprovalsPage /></Perm>} />
            <Route path="basvurular" element={<Perm perm="basvurular"><LeadsPage /></Perm>} />
            <Route path="tedarikci-basvurulari" element={<Perm perm="basvurular"><SupplierAppsPage /></Perm>} />
            <Route path="referanslar" element={<Perm perm="referanslar"><ReferencesPage /></Perm>} />
            <Route path="sss" element={<Perm perm="sss"><AdminFaqPage /></Perm>} />
            <Route path="profil" element={<ProfilePage />} />
            <Route path="yoneticiler" element={<Perm perm="kullanicilar"><AdminUsersPage /></Perm>} />
            <Route path="ayarlar" element={<Perm perm="teklifler"><SettingsPage /></Perm>} />
            <Route path="takim" element={<Perm perm="teklifler"><TeamPage /></Perm>} />
          </Route>
          <Route
            path="/tedarikci"
            element={
              <Protected role="supplier">
                <PortalLayout />
              </Protected>
            }
          >
            <Route path="dashboard" element={<SupplierDashboardPage />} />
            <Route path="talepler" element={<SupplierRequestsPage />} />
            <Route path="talepler/:id" element={<SupplierRequestDetailPage />} />
            <Route path="sss" element={<SupplierFaqPage />} />
            <Route path="profil" element={<ProfilePage />} />
          </Route>
          <Route
            path="/musteri"
            element={
              <Protected role="customer">
                <PortalLayout />
              </Protected>
            }
          >
            <Route path="projeler" element={<CustomerProjectsPage />} />
            <Route path="projeler/:id" element={<CustomerProjectDetailPage />} />
            <Route path="proje-olustur" element={<CustomerProjectCreatePage />} />
            <Route path="dashboard" element={<CustomerDashboardPage />} />
            <Route path="teklif-al" element={<QuoteRequestPage />} />
            <Route path="teklifler" element={<RequestsPage />} />
            <Route path="teklifler/:id" element={<CompareRequestPage />} />
            <Route path="sss" element={<SupplierFaqPage />} />
            <Route path="profil" element={<ProfilePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors theme="light" />
    </AuthProvider>
  );
}

export default App;
